import { isNavigationFailure } from 'vue-router';
import type { Route } from 'vue-router';

/**
 * For `router.push(…).catch(ignoreNavigationFailure)`: a navigation that a
 * guard stopped (e.g. «Esperar» on the setup pages' «Estamos guardando»
 * dialog), a duplicate or a redirect is not an error, so it must not reach
 * the global unhandled-rejection logger. Any other error is rethrown.
 */
export function ignoreNavigationFailure(error: unknown): void {
  if (!isNavigationFailure(error)) throw error;
}

/**
 * The navigation stays on the same page (the same matched route records),
 * so vue-router reuses the page component and never calls its
 * `beforeRouteLeave` (e.g. `/a/setup` → `/b/setup`, or only the query
 * changes). False for the first navigation (nothing matched before).
 */
export function reusesPage(to: Route, from: Route): boolean {
  return (
    to.matched.length > 0 &&
    to.matched.length === from.matched.length &&
    to.matched.every((record, index) => record === from.matched[index])
  );
}

type TLeaveGuard = () => Promise<boolean>;

/** Leave guards of the mounted pages that a restaurant switch reuses. */
const reusedPageGuards = new Set<TLeaveGuard>();

/**
 * Registers a page's «leave?» question for navigations that reuse the page
 * (the global guard swaps the restaurant before in-component guards run, so
 * it asks here first). Returns the unregister function.
 */
export function registerReusedPageLeaveGuard(guard: TLeaveGuard): () => void {
  reusedPageGuards.add(guard);
  return () => {
    reusedPageGuards.delete(guard);
  };
}

/** True when every mounted page allows leaving (asked one after another). */
export async function confirmReusedPageLeave(): Promise<boolean> {
  for (const guard of [...reusedPageGuards]) if (!(await guard())) return false;
  return true;
}
